/**
 * TRACKING INTERCEPTOR - DEBUG VERSION
 *
 * Slim version that ONLY logs tracking events
 * No blocking logic, just pure logging to understand what's being caught
 *
 * Generic interceptor approach - all SDKs use the same wrapper factory
 */

(function () {
  'use strict';

  const disableProductionTracking = [
    '8275647725613', //AU
    '8259015344173', //AU
    '8259015245869', //AU
    '8248282153005', //AU
    '44182560768045', //AU
    '8839648510129', //NZ
  ];

  // Debug enabled
  let DEBUG = false;
  let SHOWSTATE = false;

  // Central logging stats
  const stats = {
    eventsByType: {},
    eventDetails: [],
    blockedEvents: [],
    successEvents: [],
  };

  /**
   * HELPER FUNCTIONS FOR EVENT TRACKING
   */

  // Function to track successful events
  const trackSuccessEvent = (source, command, eventData) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      source,
      command,
      eventData,
    };

    if (SHOWSTATE) {
      stats.eventsByType[source] = (stats.eventsByType[source] || 0) + 1;
      stats.eventDetails.push(logEntry);
      stats.successEvents.push(logEntry);

      console.log(
        `[TRACKING-DEBUG ${timestamp}] ${source.toUpperCase()} > ${command}`,
        eventData,
      );
    }
  };

  // Function to track blocked events
  const trackBlockedEvent = (source, reason, eventData) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      source,
      reason,
      eventData,
    };

    if (DEBUG) {
      stats.blockedEvents.push(logEntry);

      console.log(
        `[TRACKING-BLOCKED ${timestamp}] ${source.toUpperCase()} - ${reason}`,
        eventData,
      );
    }
  };

  /**
   * GENERIC LOGGER - All interceptors use this
   */
  const logger = (source, command, eventData) => {
    trackSuccessEvent(source, command, eventData);
  };

  /**
   * HELPER FUNCTIONS FOR PRODUCT ID DETECTION
   */

  // Simple function to check if any disabled product IDs are present in the arguments
  function containsDisabledProducts(argumentsList) {
    try {
      // Convert all arguments to a JSON string
      const argsString = JSON.stringify(argumentsList);

      // Check if any disabled product ID exists in the string
      return disableProductionTracking.some((productId) =>
        argsString.includes(productId),
      );
    } catch (error) {
      // If JSON.stringify fails (e.g., circular reference), fallback to string conversion
      const argsString = String(argumentsList);
      return disableProductionTracking.some((productId) =>
        argsString.includes(productId),
      );
    }
  }

  // Simple function to check if gtag event contains "Swatch" products
  function containsSwatchProducts(argumentsList) {
    try {
      const argsString = JSON.stringify(argumentsList).toLowerCase();
      return argsString.includes('swatch');
    } catch (error) {
      const argsString = String(argumentsList).toLowerCase();
      return argsString.includes('swatch');
    }
  }

  /**
   * GENERIC SDK WRAPPER FACTORY
   * Creates a wrapper function for any SDK
   *
   * @param {string} sdkName - Name of the SDK (fbq, ttq, gtag, etc)
   * @param {Function} originalFn - Original SDK function
   * @param {Array<string>} trackingCommands - Commands to log (e.g., ['track', 'trackCustom'])
   * @param {Function} dataExtractor - Function to extract event data from args
   */
  function createSDKWrapper(
    sdkName,
    originalFn,
    trackingCommands,
    dataExtractor,
  ) {
    return function (...args) {
      const command = args[0];

      if (trackingCommands.includes(command)) {
        const eventData = dataExtractor(args);
        logger(sdkName, command, eventData);
      }

      return originalFn.apply(this, args);
    };
  }

  /**
   * LEVEL 1: Fetch interceptor
   */
  function interceptFetch() {
    const originalFetch = window.fetch;

    window.fetch = function (...args) {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : resource.url;

      const trackingDomains = [
        'facebook.com',
        'google-analytics.com',
        'analytics.tiktok.com',
        'pinterest.com',
        'googleadservices.com',
        'doubleclick.net',
        'shopify',
        'monorail',
        'kochava',
      ];

      const isTracking = trackingDomains.some((domain) => url.includes(domain));

      if (isTracking) {
        const body = config?.body || '';
        logger('fetch', url, { body: body.toString().substring(0, 200) });
      }

      return originalFetch.apply(this, args);
    };

    console.log('[TRACKING-DEBUG] ✓ Fetch interceptor installed');
  }

  /**
   * LEVEL 2: XHR interceptor
   */
  function interceptXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._debugURL = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const url = this._debugURL || '';

      const trackingDomains = [
        'facebook.com',
        'google-analytics.com',
        'analytics.tiktok.com',
        'pinterest.com',
        'googleadservices.com',
        'doubleclick.net',
      ];

      const isTracking = trackingDomains.some((domain) => url.includes(domain));

      if (isTracking) {
        logger('xhr', url, { body: body?.toString().substring(0, 200) });
      }

      return originalSend.call(this, body);
    };

    console.log('[TRACKING-DEBUG] ✓ XHR interceptor installed');
  }

  /**
   * LEVEL 3: GTM interceptor with Swatch blocking
   */
  function interceptGTM() {
    const checkGTM = setInterval(() => {
      if (window.dataLayer) {
        clearInterval(checkGTM);

        const originalPush = window.dataLayer.push;

        window.dataLayer.push = function (...args) {
          const event = args[0];
          if (event && typeof event === 'object') {
            // Check if arguments contain "Swatch" products using string search
            const hasSwatchItems = containsSwatchProducts(args);

            // If Swatch items are found, don't track the event
            if (hasSwatchItems) {
              trackBlockedEvent('gtm', 'Contains Swatch product', {
                event: event.event,
                data: event,
              });
              return;
            }

            // Track successful event
            trackSuccessEvent('gtm', 'push', {
              event: event.event,
              ...event,
            });
          }
          return originalPush.apply(this, args);
        };

        console.log('[TRACKING-DEBUG] ✓ GTM interceptor installed');
      }
    }, 100);
  }

  /**
   * LEVEL 4: Shopify Web Pixels
   */
  function interceptShopifyPixels() {
    if (window.addEventListener) {
      window.addEventListener(
        'shopify_pixel',
        (event) => {
          const detail = event.detail || {};
          logger('shopify', 'pixel_event', detail);
        },
        true,
      );
    }

    console.log('[TRACKING-DEBUG] ✓ Shopify Web Pixels interceptor installed');
  }

  /**
   * LEVEL 5: Message Channel
   */
  function interceptMessageChannel() {
    const originalPostMessage = window.postMessage;

    window.postMessage = function (...args) {
      const [message, targetOrigin] = args;

      if (message && typeof message === 'object') {
        // logger('message', 'postMessage', {
        //   type: message.type,
        //   targetOrigin,
        //   payload: JSON.stringify(message).substring(0, 200),
        // });
      }

      return originalPostMessage.apply(this, args);
    };

    console.log('[TRACKING-DEBUG] ✓ Message Channel interceptor installed');
  }

  /**
   * LEVEL 6: Pixel SDKs (Facebook, TikTok, Google, Pinterest, Klaviyo)
   */
  function interceptPixelSDKs() {
    // Facebook Pixel with blocking logic and property interception
    setupFacebookInterceptor();

    // Klaviyo with blocking logic and property interception
    setupKlaviyoInterceptor();

    // TikTok Pixel
    if (window.ttq) {
      window.ttq = createSDKWrapper(
        'ttq',
        window.ttq,
        ['track', 'trackCustom'],
        (args) => ({
          event: args[1],
          data: args[2],
        }),
      );
      console.log('[TRACKING-DEBUG] ✓ TikTok Pixel interceptor installed');
    }

    // Google Analytics (gtag) with Swatch blocking logic
    if (window.gtag) {
      const originalGtag = window.gtag;
      window.gtag = function (command, ...args) {
        // Check if arguments contain "Swatch" products using string search
        const hasSwatchItems = containsSwatchProducts([command, ...args]);

        // If Swatch items are found, don't track the event
        if (hasSwatchItems) {
          trackBlockedEvent('gtag', 'Contains Swatch product', {
            command: command,
            args: args,
          });
          return;
        }

        // Track successful event
        trackSuccessEvent('gtag', command, {
          command: command,
          args: args,
        });

        return originalGtag.apply(this, arguments);
      };
      console.log(
        '[TRACKING-DEBUG] ✓ Google Analytics (gtag) interceptor installed',
      );
    }

    // Pinterest Pixel
    if (window.pintrk) {
      window.pintrk = createSDKWrapper(
        'pintrk',
        window.pintrk,
        ['track'],
        (args) => ({
          event: args[1],
          data: args[2],
        }),
      );
      console.log('[TRACKING-DEBUG] ✓ Pinterest Pixel interceptor installed');
    }
  }

  /**
   * FACEBOOK PIXEL INTERCEPTOR SETUP
   */
  function setupFacebookInterceptor() {
    const fbqNames = ['fbq', '_fbq'];
    const trackedValues = {};

    fbqNames.forEach((name) => {
      // Store current value
      trackedValues[name] = window[name];

      // Proxy existing value if it exists
      if (window[name]) {
        const proxiedValue = createFacebookProxy(window[name]);
        Object.defineProperty(window, name, {
          value: proxiedValue,
          writable: true,
          configurable: true,
        });
      }

      // Watch for later initialization
      Object.defineProperty(window, name, {
        get: function () {
          return trackedValues[name];
        },
        set: function (newValue) {
          console.log(`✨ ${name} being set/initialized`);
          trackedValues[name] = createFacebookProxy(newValue);
        },
        configurable: true,
      });
    });

    console.log('[TRACKING-DEBUG] ✓ Facebook Pixel interceptor installed');
  }

  /**
   * KLAVIYO INTERCEPTOR SETUP
   */
  function setupKlaviyoInterceptor() {
    const klaviyoNames = ['klaviyo'];
    const trackedValues = {};

    klaviyoNames.forEach((name) => {
      // Store current value
      trackedValues[name] = window[name];

      // Proxy existing value if it exists
      if (window[name]) {
        const proxiedValue = createKlaviyoProxy(window[name]);
        Object.defineProperty(window, name, {
          value: proxiedValue,
          writable: true,
          configurable: true,
        });
      }

      // Watch for later initialization
      Object.defineProperty(window, name, {
        get: function () {
          return trackedValues[name];
        },
        set: function (newValue) {
          console.log(`✨ ${name} being set/initialized`);
          trackedValues[name] = createKlaviyoProxy(newValue);
        },
        configurable: true,
      });
    });

    console.log('[TRACKING-DEBUG] ✓ Klaviyo interceptor installed');
  }

  /**
   * FACEBOOK PIXEL PROXY CREATOR
   */
  function createFacebookProxy(original) {
    if (!original) return;

    return new Proxy(original, {
      apply: function (target, thisArg, argumentsList) {
        const facebookEventsList = [
          'PageView',
          'ViewContent',
          'Search',
          'AddToCart',
          'InitiateCheckout',
          'AddPaymentInfo',
          'Purchase',
        ];

        // Check if any disabled product ID exists in the arguments
        const isDisabled = containsDisabledProducts(argumentsList);

        // If disabled, don't execute the tracking
        if (isDisabled) {
          trackBlockedEvent('fbq', 'Contains disabled product ID', {
            command: argumentsList[0],
            event: argumentsList[1],
            data: argumentsList[2],
          });
          return;
        }

        // Check if any argument is a valid Facebook event to track in success
        const hasValidEvent = argumentsList
          .filter((arg) => typeof arg === 'string')
          .some((event) => facebookEventsList.includes(event));

        // Only track in success if it's a relevant Facebook event
        if (hasValidEvent) {
          trackSuccessEvent('fbq', argumentsList[0], {
            event: argumentsList[1],
            data: argumentsList[2],
          });
        }

        return Reflect.apply(target, thisArg, argumentsList);
      },
      get: function (target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);

        if (typeof value === 'function') {
          return function (...args) {
            const facebookEventsList = [
              'PageView',
              'ViewContent',
              'Search',
              'AddToCart',
              'InitiateCheckout',
              'AddPaymentInfo',
              'Purchase',
            ];

            // Check if any disabled product ID exists in the arguments
            const isDisabled = containsDisabledProducts(args);

            // If disabled, don't execute the tracking
            if (isDisabled) {
              trackBlockedEvent('fbq', 'Contains disabled product ID', {
                method: String(prop),
                args: args,
              });
              return;
            }

            // Check if any argument is a valid Facebook event to track in success
            const hasValidEvent = args
              .filter((arg) => typeof arg === 'string')
              .some((event) => facebookEventsList.includes(event));

            // Only track in success if it's a relevant Facebook event
            if (hasValidEvent) {
              trackSuccessEvent('fbq', String(prop), {
                method: String(prop),
                args: args,
              });
            }

            return value.apply(target, args);
          };
        }

        return value;
      },
    });
  }

  /**
   * KLAVIYO PROXY CREATOR
   */
  function createKlaviyoProxy(original) {
    if (!original) return;

    return new Proxy(original, {
      apply: function (target, thisArg, argumentsList) {
        const klaviyoEventsList = [
          'Viewed Product',
          '__activity__',
          'Added to Cart',
          'AddToCart',
        ];

        // Check if any disabled product ID exists in the arguments
        const isDisabled = containsDisabledProducts(argumentsList);

        // If disabled, don't execute the tracking
        if (isDisabled) {
          trackBlockedEvent('klaviyo', 'Contains disabled product ID', {
            event: argumentsList[0],
            data: argumentsList.slice(1),
          });
          return;
        }

        // Check if any argument is a valid Klaviyo event to track in success
        const hasValidEvent = argumentsList
          .filter((arg) => typeof arg === 'string')
          .some((event) => klaviyoEventsList.includes(event));

        // Only track in success if it's a relevant Klaviyo event
        if (hasValidEvent) {
          trackSuccessEvent('klaviyo', argumentsList[0], {
            event: argumentsList[0],
            data: argumentsList.slice(1),
          });
        }

        return Reflect.apply(target, thisArg, argumentsList);
      },
      get: function (target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);

        if (typeof value === 'function') {
          return function (...args) {
            const klaviyoEventsList = [
              'Viewed Product',
              '__activity__',
              'Added to Cart',
              'AddToCart',
            ];

            // Check if any disabled product ID exists in the arguments
            const isDisabled = containsDisabledProducts(args);

            // If disabled, don't execute the tracking
            if (isDisabled) {
              trackBlockedEvent('klaviyo', 'Contains disabled product ID', {
                method: String(prop),
                args: args,
              });
              return;
            }

            // Check if any argument is a valid Klaviyo event to track in success
            const hasValidEvent = args
              .filter((arg) => typeof arg === 'string')
              .some((event) => klaviyoEventsList.includes(event));

            // Only track in success if it's a relevant Klaviyo event
            if (hasValidEvent) {
              trackSuccessEvent('klaviyo', String(prop), {
                method: String(prop),
                args: args,
              });
            }

            return value.apply(target, args);
          };
        }

        return value;
      },
    });
  }

  /**
   * Initialize all interceptors
   */
  function initialize() {
    console.log('[TRACKING-DEBUG] 🚀 Initializing debug interceptors...');

    // Stage 1: Immediate
    interceptFetch();
    interceptXHR();
    interceptPixelSDKs();
    interceptMessageChannel();

    // Stage 2: After DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        interceptGTM();
        interceptShopifyPixels();
      });
    } else {
      interceptGTM();
      interceptShopifyPixels();
    }

    // Stage 3: After page load
    window.addEventListener('load', () => {
      console.log('[TRACKING-DEBUG] ✅ All debug interceptors initialized');
      window.__trackingDebugStats = stats;
    });
  }

  /**
   * Public API
   */
  window.__trackingDebug = {
    getStats: () => stats,
    getBlockedEvents: () => stats.blockedEvents,
    getSuccessEvents: () => stats.successEvents,
    resetStats: () => {
      stats.eventsByType = {};
      stats.eventDetails = [];
      stats.blockedEvents = [];
      stats.successEvents = [];
    },
    setDebug: (enabled) => {
      DEBUG = enabled;
    },
    printStats: () => {
      console.table(stats.eventsByType);
      console.log('Detailed events:', stats.eventDetails);
      console.log('Blocked events:', stats.blockedEvents);
      console.log('Success events:', stats.successEvents);
    },
  };

  // Initialize
  initialize();
})();
